// Question.tsx mit clickable privacy_policy_term in der Beschreibung
import { QuestionConfig, QuestionType } from "../../data/QuestionConfig";
import SingleMultiChoiceQuestion from "./SingleMultiChoiceQuestion.tsx";
import NumericMultiNumericQuestion from "./NumericMultiNumericQuestion.tsx";
import PrivacyAgreementQuestion from "./PrivacyAgreementQuestion";
import { Box, Typography, Tooltip, Dialog, DialogContent, IconButton, Link } from "@mui/material";
import { useTranslation } from "react-i18next";
import { InfoOutlined, Close } from "@mui/icons-material";
import { useState } from "react";
import InfoPopupContent from "./InfoPopupContent.tsx";
import { useTheme } from "@mui/material/styles";

interface QuestionProps {
    questionConfig: QuestionConfig;
    onNewsletterChange: (subscribeNewsletter: boolean) => void;
    onEmailChange: (email: string) => void;
    onRegionChange: (region: string) => void;
    onAgreePrivacyChange: (agreePrivacy: boolean) => void;
    onNameChange?: (name: string) => void; // Neue Prop hinzufügen, optional machen
}

export default function Question({ questionConfig,
                                     onNewsletterChange,
                                     onEmailChange,
                                     onRegionChange,
                                     onAgreePrivacyChange,
                                     onNameChange = () => {} // Standardwert für optionale Prop
                                 }: QuestionProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    let content;

    if (questionConfig.questionId === "privacy_agreement") {
        content = (
            <PrivacyAgreementQuestion
                onSubscribeNewsletterChange={(subscribeNewsletter) => onNewsletterChange(subscribeNewsletter)}
                onEmailChange={(email) => onEmailChange(email)}
                onRegionChange={(region) => onRegionChange(region)}
                onAgreePrivacyChange={(agreePrivacy) => onAgreePrivacyChange(agreePrivacy)}
                questionConfig={questionConfig}
                onNameChange={onNameChange}
            />
        );
    } else if (
        questionConfig.questionType === QuestionType.singleChoice ||
        questionConfig.questionType === QuestionType.multiChoice
    ) {
        content = <SingleMultiChoiceQuestion questionConfig={questionConfig} />;
    } else if (
        questionConfig.questionType === QuestionType.multiNumeric ||
        questionConfig.questionType === QuestionType.numeric
    ) {
        content = <NumericMultiNumericQuestion questionConfig={questionConfig} />;
    } else {
        content = null; // falls ein ungültiger Typ vorliegt
    }

    const showInfoIcon = questionConfig.infoText;

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    // Funktion, um den Beschreibungstext mit klickbarem Link zu rendern, falls notwendig
    const renderDescription = () => {
        const descriptionText = t(questionConfig.description);
        const privacyPolicyWord = t("privacy_policy_term");

        // Nur für privacy_agreement und wenn der Text das Datenschutzpolicy-Wort enthält
        if (questionConfig.questionId === "privacy_agreement" && descriptionText.includes(privacyPolicyWord)) {
            const parts = descriptionText.split(privacyPolicyWord);
            return (
                <>
                    {parts[0]}
                    <Link
                        onClick={handleOpen}
                        underline="always"
                        sx={{
                            cursor: 'pointer',
                            color: theme.palette.primary.main,
                            fontWeight: 'bold',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {privacyPolicyWord}
                    </Link>
                    {parts[1]}
                </>
            );
        }

        // Standard: Text ohne Verlinkung
        return descriptionText;
    };

    return (
        <Box sx={{ height: '100%', overflow: 'auto' }}>
            {/* Container für Überschrift mit Info-Button */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 0
                }}
            >
                <Typography variant="h4" component="span" sx={{ textAlign: 'center' }}>
                    {t(questionConfig.questionHeadline)}
                </Typography>
            </Box>

            {showInfoIcon && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: 'center',
                        mb: 0
                    }}
                >
                    <Tooltip title={t('info_tooltip')}>
                        <IconButton onClick={handleOpen}>
                            <InfoOutlined />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Container für Description */}
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    my: 1,
                    mt: 0
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        maxWidth: '70%'
                    }}
                >
                    {renderDescription()}
                </Typography>
            </Box>

            {content}

            <Dialog open={open} onClose={handleClose}
                    maxWidth="xl"
                    fullWidth
            >
                <DialogContent
                    sx={{
                        padding: 3,
                        maxHeight: '100vh',
                        width: '100%',
                        overflow: 'auto'
                    }}
                >
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6">{t('info_dialog_title')}</Typography>
                        <IconButton onClick={handleClose} aria-label={t('info_dialog_close')}>
                            <Close />
                        </IconButton>
                    </Box>
                    <Typography variant="body1">{questionConfig.detailedInfoPopupDescription ? t(questionConfig.detailedInfoPopupDescription) : ""}</Typography>

                    <InfoPopupContent questionConfig={questionConfig} />

                </DialogContent>
            </Dialog>
        </Box>
    );
}